import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const invoiceKeys = {
  all: ['invoices'],
  grouped: (filters) => [...invoiceKeys.all, 'grouped', filters],
  pdf: (id) => [...invoiceKeys.all, 'pdf', id],
  export: (filters) => [...invoiceKeys.all, 'export', filters],
};

/**
 * Hook para obtener agrupaciones de facturas
 * @param {Object} filters - Filtros con start_date, end_date y legal_entity
 */
export const useGroupedInvoices = (filters = {}) => {
  return useQuery({
    queryKey: invoiceKeys.grouped(filters),
    queryFn: async () => {
      const response = await apiClient.get('/invoices/grouped', {
        params: filters,
      });
      return response.data;
    },
    enabled: !!filters.start_date && !!filters.end_date && !!filters.legal_entity,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook para crear una nueva factura
 */
export const useCreateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceData) => {
      const response = await apiClient.post('invoices', invoiceData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.grouped() });
    },
    onError: (error) => {
      console.error('Error creating invoice:', error);
    },
  });
};

/**
 * Hook para imprimir/descargar una factura en PDF
 */
export const usePrintInvoice = () => {
  return useMutation({
    mutationFn: async (uuid_invoice) => {
      const response = await apiClient.get(`/invoices/print/${uuid_invoice}`);

      const { pdf_base64, download_name } = response.data;

      const byteCharacters = atob(pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', download_name);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      return response.data;
    },
    onError: (error) => {
      console.error('Error printing invoice:', error);
    },
  });
};

/**
 * Hook para exportar cabeceras de facturas filtradas
 */
export const useExportInvoices = () => {
  return useMutation({
    mutationFn: async (filters = {}) => {
      const response = await apiClient.get('/invoices/export', {
        params: filters,
      });
      return response.data;
    },
    mutationKey: invoiceKeys.export(),
    onError: (error) => {
      console.error('Error exporting invoices:', error);
    },
  });
};

/**
 * Hook para obtener la fecha de la última factura ARCA (con CAE)
 */
export const useLastArcaInvoiceDate = () => {
  return useQuery({
    queryKey: [...invoiceKeys.all, 'last-arca-invoice-date'],
    queryFn: async () => {
      const response = await apiClient.get('/invoices/last-arca-invoice-date');
      return response.data.last_arca_export_date;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};
